import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import 'bootstrap/dist/css/bootstrap.min.css';
import { setAuth } from '../Components/Redux/authSlice';
import { RootState } from '../Components/Redux/store';
import * as XLSX from 'xlsx';
import './FeesReport.css';

interface Student {
  id: number;
  name: string;
  class_name: string;
  guardian_number: string;
  address: string;
}

interface Class {
  id: number;
  name: string;
}

interface Section {
  id: number;
  name: string;
}

// Utility: Get cookie value by name
const getCookie = (name: string) => {
  const cookieName = name + "=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');
  for (let i = 0; i < cookieArray.length; i++) {
    let cookie = cookieArray[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(cookieName) === 0) {
      return cookie.substring(cookieName.length, cookie.length);
    }
  }
  return '';
};

const FeesReport: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [month, setMonth] = useState<string>((new Date().getMonth() + 1).toString());
  const [year] = useState<number>(new Date().getFullYear());
  const [selectedClass, setSelectedClass] = useState<number | null>(null);
  const [selectedSection, setSelectedSection] = useState<number | null>(null);
  const [classes, setClasses] = useState<Class[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);



  // Fetch all classes when the component mounts
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await axios.get('/Branches/classes/');
        setClasses(response.data);
      } catch (error) {
        console.error('Error fetching classes:', error);
        setError('Failed to fetch classes');
      }
    };

    fetchClasses();
  }, []);

  // Fetch sections whenever a class is selected
  useEffect(() => {
    if (selectedClass !== null) {
      const fetchSections = async () => {
        try {
          const response = await axios.get(
            `/Branches/sections/?class_id=${selectedClass}`
          );
          setSections(response.data);
        } catch (error) {
          console.error('Error fetching sections:', error);
          setError('Failed to fetch sections');
        }
      };
      fetchSections();
    } else {
      setSections([]);
      setSelectedSection(null);
    }
  }, [selectedClass]);

  // Fetch students with unpaid fees whenever month, class, or section changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }
        setLoading(true);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Build the API URL dynamically based on filters
        let url = `/fees/fees_not_paid/?month=${month}&year=${year}&class_id=${selectedClass}`;
        if (selectedSection !== null) {
          url += `&section_id=${selectedSection}`;
        }

        const response = await axios.get(url, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        console.log('Fetched students:', response.data);
        setStudents(response.data || []);
        setLoading(false);
        dispatch(setAuth(true));
      } catch (error) {
        console.error('Error fetching students:', error);
        setLoading(false);
        setError('Failed to fetch data');
      }
    };

    if (selectedClass !== null) {
      fetchData();
    }
  }, [month, year, selectedClass, selectedSection, dispatch]);

  // Handlers for select changes
  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setMonth(event.target.value);
  };

  const handleClassChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    if (value === '') {
      setSelectedClass(null);
      setSections([]);
      setSelectedSection(null);
    } else {
      setSelectedClass(Number(value));
    }
  };

  const handleSectionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    if (value === '') {
      setSelectedSection(null);
    } else {
      setSelectedSection(Number(value));
    }
  };

  // Report generation functions
  const generatePDF = () => {
    const doc = new jsPDF();
    doc.text('Warbixinta Lacagaha', 14, 16);
    doc.autoTable({
      head: [['Magaca', 'Fasalka', 'Lambarka Waalidka', 'Cinwaanka']],
      body: students.map((student) => [
        student.name,
        student.class_name,
        student.guardian_number,
        student.address,
      ]),
      startY: 20,
    });
    doc.save('fees_class_report.pdf');
  };

  const printReport = () => {
    const printWindow = window.open('', '', 'height=600,width=800');
    if (printWindow) {
      printWindow.document.write('<html><head><title>Warbixinta Lacagaha</title>');
      printWindow.document.write(
        '<link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.5.2/css/bootstrap.min.css">'
      );
      printWindow.document.write('</head><body>');
      printWindow.document.write('<div class="container">');
      printWindow.document.write('<h1 class="my-4">Warbixinta Lacagaha</h1>');
      printWindow.document.write('<table class="table table-bordered">');
      printWindow.document.write(
        '<thead><tr><th>Magaca</th><th>Fasalka</th><th>Lambarka Waalidka</th><th>Cinwaanka</th></tr></thead>'
      );
      printWindow.document.write('<tbody>');
      students.forEach(student => {
        printWindow.document.write(`<tr>
          <td>${student.name}</td>
          <td>${student.class_name}</td>
          <td>${student.guardian_number}</td>
          <td>${student.address}</td>
        </tr>`);
      });
      printWindow.document.write('</tbody></table>');
      printWindow.document.write('</div>');
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(students.map(student => ({
      Magaca: student.name,
      Fasalka: student.class_name,
      Lambarka_Waalidka: student.guardian_number,
      Cinwaanka: student.address,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Warbixinta Lacagaha');
    XLSX.writeFile(wb, 'fees_report.xlsx');
  };

  return (
    <div className="fees-report-container">
      {/* Hero Header */}
      <div className="report-hero">
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="icon">💰</span> Warbixinta Lacagaha Aan La Bixin
          </h1>
          <p className="hero-subtitle">La soco lacagaha aan la bixin iyo heerka lacag bixinta</p>
        </div>
      </div>
      {auth ? (
        <>
          {/* Action Buttons */}
          <div className="action-buttons">
            <button className="action-btn btn-pdf" onClick={generatePDF}>
              <span className="btn-icon">📄</span> PDF
            </button>
            <button className="action-btn btn-print" onClick={printReport}>
              <span className="btn-icon">🖨️</span> Daabac
            </button>
            <button className="action-btn btn-excel" onClick={exportToExcel}>
              <span className="btn-icon">📊</span> Excel
            </button>
          </div>

          {/* Filters Card */}
          <div className="filters-card">
            <h3 className="filters-title">
              <span className="icon-small">🔍</span> Shaandhee Ardayda
            </h3>
            <div className="row g-3">
              <div className="col-md-4">
                <label htmlFor="classSelect" className="form-label fw-semibold">Fasalka</label>
                <select
                  id="classSelect"
                  className="form-select"
                  value={selectedClass !== null ? selectedClass : ''}
                  onChange={handleClassChange}
                >
                  <option value="" disabled>Dooro fasal</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedClass !== null && (
                <div className="col-md-4">
                  <label htmlFor="sectionSelect" className="form-label fw-semibold">Qeybta</label>
                  <select
                    id="sectionSelect"
                    className="form-select"
                    value={selectedSection !== null ? selectedSection : ''}
                    onChange={handleSectionChange}
                  >
                    <option value="">Dhammaan Qeybaha</option>
                    {sections.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="col-md-4">
                <label htmlFor="monthSelect" className="form-label fw-semibold">Bisha</label>
                <select
                  id="monthSelect"
                  className="form-select"
                  value={month}
                  onChange={handleMonthChange}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>
                      {new Date(0, m - 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Loading/Error/Data Section */}
          {loading ? (
            <div className="d-flex justify-content-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Soo dejinaya...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          ) : (
            <div className="card shadow-sm">
              <div className="card-body p-0">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th scope="col">Magaca</th>
                      <th scope="col">Fasalka</th>
                      <th scope="col">Lambarka Waalidka</th>
                      <th scope="col">Cinwaanka</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-4">
                          <i className="bi bi-currency-exchange display-4 text-muted"></i>
                          <p className="mt-3 mb-0 text-muted">
                            {selectedClass
                              ? "Dhammaan lacagaha waa la bixiyay shaandhaynta la doortay ama lama helin arday."
                              : "Fadlan dooro fasal si aad u aragto warbixinta lacagaha."}
                          </p>
                        </td>
                      </tr>
                    ) : (
                      students.map((student) => (
                        <tr key={student.id}>
                          <td className="fw-semibold">{student.name}</td>
                          <td>{student.class_name}</td>
                          <td>{student.guardian_number}</td>
                          <td>{student.address}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="card shadow-sm">
          <div className="card-body text-center py-5">
            <i className="bi bi-shield-lock display-4 text-muted"></i>
            <p className="mt-3 mb-0 text-muted">
              Fadlan soo gal si aad u aragto warbixinta lacagaha.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeesReport;